/**
 * Central help registry — one entry per page/tab in the app. Authored once and
 * rendered in two places: the per-page slide-over (HelpButton/HelpPanel) and the
 * page-by-page guide on /help. Static content; ships + versions with the UI.
 *
 * Topic ids are stable (used as /help anchors and as the `topic` prop on
 * HelpButton). Screenshots are referenced as /help/<id>.png (served same-origin
 * from apps/frontend/public/help/); a missing image degrades to a placeholder.
 */
import { K, type HelpEntry, type NavGroup } from "./components";

export const NAV_GROUP_LABEL: Record<NavGroup, string> = {
  main: "Main",
  chassis: "Chassis page",
  tools: "Tools",
  settings: "Settings",
};

const shot = (id: string, caption: string) => [{ src: `/help/${id}.png`, caption }];

export const HELP_TOPICS: Record<string, HelpEntry> = {
  // ---------------------------------------------------------------- main
  home: {
    id: "home",
    title: "Home — the fleet",
    route: "/",
    navGroup: "main",
    summary:
      "Your whole fleet of chassis (OLTs), grouped by Gigafy location. The landing page for triage — scan health at a glance and jump straight into a chassis.",
    items: [
      { label: "Health pills", body: "Per-chassis good / warning / critical rollup from the latest scans." },
      { label: "VDSL line-error badges", body: "On VDSL chassis: how many ports are flagged critical (red) or warning (amber) for line errors over the last 7 days. Shown only when something is flagged." },
      { label: "Alarm badge", body: "Count of unacknowledged alarms on that chassis." },
      { label: "⚠ N pending", body: "Shown when a chassis has discovered-but-unprovisioned ONUs waiting on the Pending tab." },
      { label: "Quick-job buttons", body: "Fire common jobs (list, collect, backup) without opening the chassis." },
      { label: "Card → chassis", body: "Click a card to open that chassis's page." },
    ],
    shots: shot("home", "Home: fleet grouped by location with health + pending badges."),
    related: [
      { label: "Locations", to: "/locations" },
      { label: "Fleet alarms", to: "/alarms" },
    ],
  },
  locations: {
    id: "locations",
    title: "Locations",
    route: "/locations",
    navGroup: "main",
    summary: "The list of Gigafy locations the tool knows about. Each location groups the chassis (and adoptable devices) at that site.",
    items: [
      { label: "Location row", body: "Opens the location detail with its devices + adoption controls." },
      { label: "Device counts", body: "How many chassis/devices are associated with each location." },
    ],
    shots: shot("locations", "Locations list."),
    related: [{ label: "Home", to: "/" }],
  },
  "location-detail": {
    id: "location-detail",
    title: "Location detail",
    route: "/locations/:id",
    navGroup: "main",
    summary: "A single Gigafy location: its devices and the controls to adopt a device into the tool as a managed chassis.",
    items: [
      { label: "Device list", body: "Devices published at this location (from Gigafy)." },
      { label: "Adopt", body: "Bring a device under management as a chassis (sets adapter + credentials chain)." },
    ],
    shots: shot("location-detail", "Location detail with device adoption."),
    related: [{ label: "Locations", to: "/locations" }],
  },
  alarms: {
    id: "alarms",
    title: "Fleet alarms",
    route: "/alarms",
    navGroup: "main",
    summary: "Cross-chassis alarm triage — every active alarm across the fleet in one place, reachable from the navbar bell.",
    items: [
      { label: "Severity", body: <>Driven by <K>Settings → Alarm rules</K> (pattern → severity).</> },
      { label: "Acknowledge / Mute", body: "Clear an alarm from the bell count or silence a noisy one." },
      { label: "Chassis link", body: "Jump to the chassis the alarm came from." },
    ],
    shots: shot("alarms", "Fleet-wide alarm triage."),
    related: [{ label: "Alarm rules", to: "/settings/alarm-rules" }],
  },

  // ------------------------------------------------------------- chassis
  "chassis-inventory": {
    id: "chassis-inventory",
    title: "Chassis · Inventory",
    route: "/chassis/:id — Inventory tab",
    navGroup: "chassis",
    summary: "The port grid for this chassis. GPON shows ONUs (display id, serial, state, optical RX, last MAC); VDSL shows ports (line state, up/down rates, SNR).",
    items: [
      { label: "List ONUs / ports", body: "Refresh the grid live from the device." },
      { label: "Collect optical / line speed", body: "Pull health (GPON RX power / VDSL rates + SNR)." },
      { label: "Collect MAC table", body: "Map subscriber MACs to ports." },
      { label: "⚠ errors chip (VDSL)", body: "The VDSL equivalent of a low-optical ONU: amber = warning, red = critical, based on the port’s last 7 days of error counters (ES / SES / UAS / FEC seconds / retrains). Hover for the totals and the reasons. Hidden when the line is clean or has under a day of history. The “N flagged” button in the card header filters the grid to flagged ports." },
      { label: "Click a cell", body: "Copies its value." },
      { label: "ⓘ expand", body: "Expand a row for MAC history + recent optical samples." },
      { label: "Run job", body: "Open the job picker for this chassis." },
    ],
    shots: shot("chassis-inventory", "Inventory: PON/VDSL port grid."),
    related: [
      { label: "Pending ONUs", to: "/" },
      { label: "Jobs", to: "/settings/jobs" },
    ],
  },
  "chassis-backups": {
    id: "chassis-backups",
    title: "Chassis · Backups",
    route: "/chassis/:id — Backups tab",
    navGroup: "chassis",
    summary: <>Snapshots of the device running-config (<K>backup_running_config</K>) with history, plus an offline config check.</>,
    items: [
      { label: "Take backup", body: "Capture the current running-config into history." },
      { label: "Verify config", body: "Cross-check ONUs vs subscriber blocks in the latest backup — no device access needed." },
      { label: "History", body: "Prior backups, newest first; profiles are parsed from the latest one." },
    ],
    shots: shot("chassis-backups", "Backups list + verify."),
    related: [{ label: "Config profiles (Deployment)", to: "/" }],
  },
  "chassis-alarms": {
    id: "chassis-alarms",
    title: "Chassis · Alarms",
    route: "/chassis/:id — Alarms tab",
    navGroup: "chassis",
    summary: <>Active alarms on this chassis. <K>Fetch alarms</K> pulls them from the device; the navbar bell reflects the unacknowledged count.</>,
    items: [
      { label: "Fetch alarms", body: "Pull current active alarms from the device." },
      { label: "Acknowledge / Mute", body: "Clear or silence individual alarms (when not read-only)." },
    ],
    shots: shot("chassis-alarms", "Per-chassis alarms."),
    related: [{ label: "Alarm rules", to: "/settings/alarm-rules" }],
  },
  "chassis-activity": {
    id: "chassis-activity",
    title: "Chassis · Activity",
    route: "/chassis/:id — Activity tab",
    navGroup: "chassis",
    summary: "Recent jobs for this chassis with their status, plus Cancel/Retry and a link into the full job detail + device transcript.",
    items: [
      { label: "Job rows", body: "Recent jobs, newest first, with status + duration." },
      { label: "Cancel / Retry", body: "Stop an in-flight job or re-queue a failed one." },
      { label: "Details →", body: "Open per-target results + the live transcript." },
    ],
    shots: shot("chassis-activity", "Activity: recent jobs."),
    related: [{ label: "All jobs", to: "/settings/jobs" }],
  },
  "chassis-admin": {
    id: "chassis-admin",
    title: "Chassis · Administration",
    route: "/chassis/:id — Administration tab",
    navGroup: "chassis",
    summary: "Per-chassis administration: credential overrides, session/concurrency limits, and chassis-scoped schedules.",
    items: [
      { label: "Credential overrides", body: "Store SSH user/pass directly (skips the Gigafy lookup for those fields)." },
      { label: "Session limit", body: "How many concurrent jobs may touch this chassis." },
      { label: "Schedules", body: "Recurring jobs scoped to this chassis." },
    ],
    shots: shot("chassis-admin", "Chassis administration."),
    related: [{ label: "Schedules", to: "/settings/schedules" }],
  },
  "chassis-provisioning": {
    id: "chassis-provisioning",
    title: "Chassis · Provisioning",
    route: "/chassis/:id — Provisioning tab (GPON)",
    navGroup: "chassis",
    summary: <>Everything that changes what's provisioned on this chassis: <strong>Pending</strong> (discovered-but-unprovisioned ONUs, whole-port batch provision), <strong>Moves</strong> (port-mismatch alarms → one-click move), <strong>Manual ops</strong>, and the parsed <strong>Config profiles</strong>.</>,
    items: [
      { label: "Pending", body: <>Pick a PON port, set services once (dropdowns from the parsed profiles), submit — one <K>provision_onu</K> job per serial with gap-first IDs. Run <K>Scan for devices</K> first.</> },
      { label: "Moves", body: "ONTs the OLT keeps discovering on a different PON than provisioned. Preview + one-click move; completed moves drop off after the next alarm poll." },
      { label: "Manual ops", body: "Move / Bulk PON clear / Replace VNET / Add service / Provision / Deprovision / Swap — all two-click confirm gated, run under a hard chassis lock. The inventory row menu deep-links here pre-filled." },
      { label: "Config profiles", body: <>Bandwidth/shaper/vnet/cosq profiles parsed from the latest backup; <K>Re-parse</K> after a fresh backup. These feed the provisioning dropdowns.</> },
    ],
    shots: shot("chassis-provisioning", "Provisioning: pending / moves / manual ops / profiles."),
    related: [{ label: "Templates", to: "/settings/templates" }],
  },
  "chassis-add-service": {
    id: "chassis-add-service",
    title: "Chassis · Add service",
    route: "/chassis/:id — Provisioning → Manual ops → Add service",
    navGroup: "chassis",
    summary: <>Append <strong>one</strong> extra VNET service to ONUs that are already provisioned — their existing services are never removed or changed. Pick a scope (single ONU, one PON port, or the whole chassis), the VNET and its rates, then <K>Preview</K> before submitting.</>,
    items: [
      { label: "Scope", body: <>Single ONU runs one <K>add_onu_service</K> job. PON port and whole chassis run an <K>add_service_from_scope</K> job that takes a fresh backup, then adds the service in batches of 5 ONUs, one batch at a time. Two failed batches in a row stop the rest.</> },
      { label: "Service", body: <>VNET options marked <em>unused</em> exist on the OLT but no ONU has them yet. Upstream and downstream default to <K>10M-1M-commit</K> when the OLT has it; cosq defaults to the chassis's most-used cosq profile.</> },
      { label: "Preview", body: "Shows how many ONUs get the service, how many already have it, have no services (skipped), are down (skipped unless included) or are missing from the backup, with a per-port breakdown and the CLI for the first ONU. A profile missing on the OLT blocks submit." },
      { label: "Backup age", body: "Preview counts come from the latest backup. A single ONU uses that backup; port and chassis runs take a fresh one first, so their final counts can differ." },
      { label: "Whole chassis", body: <>Type <K>add &lt;VNET&gt; to &lt;chassis&gt;</K> to enable submit. Tick <em>Also add to site auto-provision defaults</em> so ONUs provisioned later at this site get the service too.</> },
      { label: "Progress", body: "The job appears in the inline tracker. Port and chassis runs also show a per-batch table under the form." },
      { label: "Shortcuts", body: <>The inventory row menu (<K>⋮ → Add service</K>) opens this form for that ONU; each port header's <K>Add service</K> button opens it for the whole port.</> },
    ],
    shots: shot("chassis-add-service", "Add service: scope, service row, preview."),
    related: [{ label: "Bulk jobs", to: "/tools/bulk-jobs" }],
  },
  "chassis-diagnostics": {
    id: "chassis-diagnostics",
    title: "Chassis · Diagnostics",
    route: "/chassis/:id — Diagnostics tab (GPON)",
    navGroup: "chassis",
    summary: <>Per-chassis health surfaces: <strong>Alarms</strong> (active alarms + ack/mute) and <strong>Backups</strong> (running-config snapshots plus the offline verify-config report).</>,
    items: [
      { label: "Alarms", body: <><K>Fetch alarms</K> pulls current active alarms; the navbar bell reflects the unacknowledged count.</> },
      { label: "Backups", body: "Capture / browse running-config snapshots; profiles are parsed from the latest one." },
      { label: "Verify config", body: "Cross-check ONUs vs subscriber blocks in the latest backup — no device access needed." },
    ],
    shots: shot("chassis-diagnostics", "Diagnostics: alarms + backups + verify."),
    related: [{ label: "Alarm rules", to: "/settings/alarm-rules" }],
  },
  "onu-detail": {
    id: "onu-detail",
    title: "ONU detail",
    route: "/chassis/:id/onu/:port/:displayId",
    navGroup: "chassis",
    summary: "Everything known about a single ONU: identity, current state, optical RX/TX history, and MAC history over time.",
    items: [
      { label: "Identity", body: "Port, ONU id, display id, serial." },
      { label: "Optical history", body: "RX/TX power samples from collect_optical runs." },
      { label: "MAC history", body: "MACs seen on this ONU over time." },
    ],
    shots: shot("onu-detail", "ONU detail: optical + MAC history."),
  },
  "vdsl-port": {
    id: "vdsl-port",
    title: "VDSL port detail",
    route: "/chassis/:id/vdsl/:slot/:port",
    navGroup: "chassis",
    summary: "A single VDSL port: line state, actual up/down sync rates, and SNR margins, with history.",
    items: [
      { label: "Line state", body: "Activated / down / training." },
      { label: "Sync rates", body: "Actual up/down kbps." },
      { label: "SNR margins", body: "Up/down signal-to-noise margin (dB)." },
      { label: "Line error flag", body: "Shown above the daily-errors chart: the port’s 7-day error totals (errored / severely errored / unavailable seconds, FEC seconds, retrains = loss-of-signal failures + counter resets) and which threshold tripped. Critical: more than 100 SES, 600 UAS, 1000 ES or 5 retrains per day. Warning: at least 50 ES, 10 SES, 60 UAS or 1 retrain per day, or FEC seconds above 10% of the time. Rates are scaled by how many days of snapshots exist; under a day of history is “not enough data”. Re-evaluated after every counter collection / Refresh line and by the daily sweep." },
      { label: "SELT interpretation", body: "Each recent SELT is explained in plain English — loop length, termination (a “short” means the pair is bridged somewhere), predicted rates — and cross-checked against the live line and its error history. The verdict says whether the fix is physical (wiring/cable work), a profile change (more noise margin/INP), nothing, or inconclusive. A physical verdict needs evidence: a SELT the live line agrees with, or error history. A SELT that contradicts a healthy, error-free line is inconclusive — re-test with the modem unplugged." },
      { label: "Modem during SELT", body: "Say whether the customer’s modem was connected before running a SELT. A connected modem can distort the length and termination readings; the answer is stored with the result and the interpretation weighs it (e.g. a short measured with the modem connected is confirmed with the modem off before dispatching)." },
    ],
    shots: shot("vdsl-port", "VDSL port detail."),
  },
  terminal: {
    id: "terminal",
    title: "SSH terminal",
    route: "/chassis/:id/terminal",
    navGroup: "chassis",
    summary: "A live interactive SSH session to the chassis in the browser (xterm). Opens a fresh session each time; activity is recorded under Terminal sessions.",
    items: [
      { label: "Live shell", body: "Type CLI commands directly against the device." },
      { label: "Session log", body: <>Every session is captured — see <K>Tools → Terminal sessions</K>.</> },
    ],
    shots: shot("terminal", "In-browser SSH terminal."),
    related: [{ label: "Terminal sessions", to: "/tools/terminal-sessions" }],
  },

  // --------------------------------------------------------------- tools
  "tools-onu-lookup": {
    id: "tools-onu-lookup",
    title: "ONU lookup",
    route: "/tools/onu-lookup",
    navGroup: "tools",
    summary: "Find an ONU by serial or display id across every chassis in the fleet — useful when you know the device but not where it lives.",
    items: [
      { label: "Search", body: "Enter a serial / display id; results span all chassis." },
      { label: "Result → ONU", body: "Jump to the matching ONU detail." },
    ],
    shots: shot("tools-onu-lookup", "ONU lookup across the fleet."),
  },
  "tools-macs": {
    id: "tools-macs",
    title: "MAC map",
    route: "/tools/macs",
    navGroup: "tools",
    summary: "Cross-chassis MAC → device-name lookup. Resolve which subscriber/device a MAC belongs to from the collected MAC tables.",
    items: [
      { label: "Search a MAC", body: "Returns the device name + chassis/port it was last seen on." },
      { label: "Freshness", body: <>Depends on recent <K>collect_mac</K> + <K>mikrotik_pppoe_sync</K> runs.</> },
    ],
    shots: shot("tools-macs", "MAC map lookup."),
  },
  "tools-bulk-jobs": {
    id: "tools-bulk-jobs",
    title: "Bulk jobs",
    route: "/tools/bulk-jobs",
    navGroup: "tools",
    summary: "Fan a single job out across many chassis at once; results roll up under one parent job.",
    items: [
      { label: "Pick chassis + job", body: "Choose the targets and the job to run on each." },
      { label: "Parent → children", body: "Each target gets a child job; the parent shows the rollup." },
    ],
    shots: shot("tools-bulk-jobs", "Bulk job fan-out."),
    related: [{ label: "Jobs", to: "/settings/jobs" }],
  },
  "tools-terminal-sessions": {
    id: "tools-terminal-sessions",
    title: "Terminal sessions",
    route: "/tools/terminal-sessions",
    navGroup: "tools",
    summary: "Live and historical SSH terminal sessions, with the full transcript of each.",
    items: [
      { label: "Session list", body: "Who connected to which chassis and when." },
      { label: "Transcript", body: "Line-by-line TX/RX of a session." },
    ],
    shots: shot("tools-terminal-sessions", "Terminal session log."),
  },
  "tools-locks": {
    id: "tools-locks",
    title: "Locks",
    route: "/tools/locks",
    navGroup: "tools",
    summary: "The active chassis locks — which job is holding which chassis right now. Helpful when a chassis seems busy.",
    items: [
      { label: "Lock rows", body: "Scope (chassis), holder (job/user), and whether it's a hard lock." },
    ],
    shots: shot("tools-locks", "Active chassis locks."),
  },

  // ------------------------------------------------------------ settings
  "settings-jobs": {
    id: "settings-jobs",
    title: "Jobs",
    route: "/settings/jobs",
    navGroup: "settings",
    summary: "Every queued / running / completed job, filterable by status. Open a job for per-target results, error rollup, and the device transcript.",
    items: [
      { label: "Status filter", body: "Narrow to queued / running / failed / etc." },
      { label: "Cancel / Retry", body: "Stop an in-flight job; re-queue a failed/partial/cancelled one." },
      { label: "Open → detail", body: "Per-target results + the line-by-line transcript." },
    ],
    shots: shot("settings-jobs", "Jobs list + detail."),
    related: [{ label: "Bulk jobs", to: "/tools/bulk-jobs" }],
  },
  "settings-templates": {
    id: "settings-templates",
    title: "Command templates",
    route: "/settings/templates",
    navGroup: "settings",
    summary: "The per-vendor CLI templates the worker executes (provision, deprovision, move, replace-vnet, bulk-clear) with version history and rollback.",
    items: [
      { label: "Edit → new version", body: "Each save creates a new active version; the shipped YAML is only the baseline." },
      { label: "Rollback", body: "Revert to an earlier version if a change misbehaves." },
      { label: "Caution", body: <>Removing the <K>enable</K>/<K>configure</K> prelude can break a job.</> },
    ],
    shots: shot("settings-templates", "Templates + version history."),
  },
  "settings-schedules": {
    id: "settings-schedules",
    title: "Schedules",
    route: "/settings/schedules",
    navGroup: "settings",
    summary: "Recurring jobs on a cron expression or interval — e.g. nightly backups or optical sweeps — per-chassis or global, with run history.",
    items: [
      { label: "Trigger", body: "Cron expression or interval, with optional jitter." },
      { label: "Scope", body: "A single chassis or fleet-wide." },
      { label: "Policy", body: <>What to do if a run overlaps: <K>skip_if_running</K> / <K>queue_after</K> / <K>cancel_running</K>.</> },
      { label: "Run history", body: "Past firings + their outcomes." },
    ],
    shots: shot("settings-schedules", "Schedules (cron / interval)."),
  },
  "settings-audit": {
    id: "settings-audit",
    title: "Audit log",
    route: "/settings/audit",
    navGroup: "settings",
    summary: "A read-only record of every operator action — login, provisioning, template edits, alarm ack/mute, schedule changes — with actor, outcome, target, and JSON detail.",
    items: [
      { label: "Filters", body: "By action and/or actor." },
      { label: "Detail", body: "Expand a row for the full JSON context + chassis link." },
    ],
    shots: shot("settings-audit", "Audit log."),
  },
  "settings-alarm-rules": {
    id: "settings-alarm-rules",
    title: "Alarm rules",
    route: "/settings/alarm-rules",
    navGroup: "settings",
    summary: "Pattern → severity mappings that classify incoming alarms (literal or regex). The worker applies enabled rules at parse time; first match wins, default info.",
    items: [
      { label: "Pattern", body: "Literal text or a regex matched against alarm text." },
      { label: "Severity", body: "critical / major / minor / warning / info — drives the badge colour." },
      { label: "Enable / disable", body: "Toggle a rule without deleting it." },
    ],
    shots: shot("settings-alarm-rules", "Alarm rule editor."),
    related: [{ label: "Fleet alarms", to: "/alarms" }],
  },
  "settings-api-keys": {
    id: "settings-api-keys",
    title: "API keys",
    route: "/settings/api-keys",
    navGroup: "settings",
    adminOnly: true,
    summary: <>Issue / list / revoke API keys for the read-only external lookup API (<K>GET /api/ext/subscriber/&#123;GIG-ID&#125;</K>, authenticated with <K>Authorization: Bearer &lt;key&gt;</K>). Admin-only — managed by users in the <K>CHASSIS_API_KEY_ADMINS</K> allowlist; everyone can read this page in the guide.</>,
    items: [
      { label: "Create key", body: "Names a key and shows the full plaintext ONCE — copy it immediately; only its SHA-256 hash is stored." },
      { label: "Key prefix / status", body: "Identify a key (first/last chars) and whether it's active or revoked." },
      { label: "Revoke", body: "Calls using that key start failing at once." },
      { label: "Last used", body: "When the key was last called." },
    ],
    shots: shot("settings-api-keys", "API key management (admin)."),
    related: [{ label: "External API (how to call it)", to: "/help#api" }],
  },
  "settings-vpn": {
    id: "settings-vpn",
    title: "VPN",
    route: "/settings/vpn",
    navGroup: "settings",
    adminOnly: true,
    summary: <>OpenVPN client for reaching chassis on networks the stack can't hit directly. <strong>Split-tunnel</strong> — only the listed off-net subnets use the tunnel. Admin-only (<K>CHASSIS_API_KEY_ADMINS</K>); everyone can read this page in the guide.</>,
    items: [
      { label: "Server / port / protocol", body: "OpenVPN endpoint + UDP/TCP." },
      { label: "Username / password", body: "Write-only — stored encrypted, never shown again." },
      { label: "Server verification", body: <><strong>Let&apos;s Encrypt CA + hostname</strong> (recommended) trusts the bundled Let&apos;s Encrypt roots and checks the cert is for the server address — renewals just work. <strong>Custom</strong> uses the extra directives (<K>&lt;ca&gt;</K> or a <K>peer-fingerprint</K> pin, which breaks on every renewal) plus the verify-certificate toggle.</> },
      { label: "Tunnel error", body: "If the server certificate or credentials are rejected, the navbar VPN icon turns red and this page shows the reason instead of spinning on “connecting”." },
      { label: "Off-net subnets", body: "Newline-separated CIDRs to route through the tunnel (e.g. 192.168.50.0/24)." },
      { label: "Extra directives", body: <>Inline OpenVPN config (<K>&lt;ca&gt;…&lt;/ca&gt;</K>, <K>tls-auth</K>, etc.).</> },
      { label: "Connect / Disconnect", body: "A VPN indicator appears in the navbar while up; events go to the Audit log." },
    ],
    shots: shot("settings-vpn", "VPN client config (admin)."),
    related: [{ label: "Audit log", to: "/settings/audit" }],
  },
  "settings-me": {
    id: "settings-me",
    title: "My settings",
    route: "/settings/me",
    navGroup: "settings",
    summary: "Your personal preferences: theme, local cache, and session info. Preferences are saved server-side to your account.",
    items: [
      { label: "Theme", body: "Light / dark / system." },
      { label: "Clear cache", body: "Drop the local React Query cache (force fresh fetches)." },
      { label: "Session info", body: "Your session id + CSRF token; link to the legacy v1 UI." },
    ],
    shots: shot("settings-me", "My settings."),
  },
};

/** Ordered topic ids for the page-by-page guide, grouped by nav area. */
export const HELP_GROUP_ORDER: NavGroup[] = ["main", "chassis", "tools", "settings"];

export function topicsInGroup(group: NavGroup): HelpEntry[] {
  return Object.values(HELP_TOPICS).filter((t) => t.navGroup === group);
}

export function getTopic(id: string): HelpEntry | undefined {
  return HELP_TOPICS[id];
}
